import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import FormSlide from "./components/FormSlide";
import ZipCodeSlide from "./components/zipCodeSlide";
import AnswerSlide from "./components/answerSlide";
import SuccessSlide from "./components/SuccessSlide";
import FreeTextSlide from "./components/freeTextSlide";
import InterimAnimationSlide from "./components/InterimAnimationSlide";
import ElectricityUseSlide from "./components/electricityUseSlide";
import SingleActionSlide from "./components/SingleActionSlide";
import DisqualifyingSlide from "./components/DisqualifyingSlide";
import AddressFormSlide from "./components/addressFormSlide";
import SeparateFormSlide from "./components/separatedFormSlide";
import ImageUploadSlide from "./components/ImageUploadSlide";
import useBrowserHistory from "./Hooks/useBrowerHook";
import { ConfigType, SlideType } from "./types/config";
import GTMComponent from "./components/GTMComponent";
import SlideNavigation from "./components/_basic/SlideNavigation";
import { Types } from "mongoose";
import PopoverForm from "./components/PopOverForm";
import { v4 as uuidv4 } from "uuid";
import { getHostPageUrl } from "../utils/hostPageContext";

const uniqueId = uuidv4();

const payload = new Map<string, any>();

const sendGtagEvent = (
  eventName: string,
  eventAction: string,
  eventLabel: string,
  eventValue?: string
) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      eventCategory: "Formslider",
      eventAction: eventAction,
      eventLabel: eventLabel,
      ...(eventValue !== undefined && { eventValue }),
    });
  } else {
    console.warn("Data Layer is not available");
  }
};

const sendLeadEvent = (eventName: string, leadsUserData: any) => {
  if (window.gtag) {
    window.gtag("event", eventName, {
      eventCategory: "Formslider",
      leadsUserData,
    });
  } else {
    console.warn("Google Analytics has not been loaded");
  }
};

const utmParams = Object.fromEntries(
  new URL(getHostPageUrl()).searchParams.entries()
);

const validSlideTypes = [
  "DISQUALIFYING_SLIDE",
  "SINGLE_ANSWER_SLIDE",
  "MULTI_ANSWER_SLIDE",
  "ZIP_CODE_SLIDE",
  "INTERIM_ANIMATION_SLIDE",
  "FORM_SLIDE",
  "ELECTRICITY_USE_SLIDE",
  "FREE_TEXT_SLIDE",
  "SUCCESS_SLIDE",
  "SINGLE_ACTION_SLIDE",
  "IMAGE_UPLOAD_SLIDE",
  "ADDRESS_FORM_SLIDE",
  "SEPARATED_FORM_SLIDE",
];

const filterAndRemoveSlides = (
  config: ConfigType,
  idsToRemoveAndAdd: { id: string; beforeId: string }[],
  idsToRemoveOnly: string[]
): [ConfigType, SlideType[]] => {
  if (config && config.slides) {
    const removedSlides: SlideType[] = [];
    const slidesToReinsert: { slide: SlideType; beforeId: string }[] = [];

    const updatedSlides = config.slides.filter((slide) => {
      const matchToRemoveAndAdd = idsToRemoveAndAdd.find(
        ({ id }) => id === slide.id
      );
      if (matchToRemoveAndAdd) {
        slidesToReinsert.push({
          slide,
          beforeId: matchToRemoveAndAdd.beforeId,
        });
        return false;
      }
      if (idsToRemoveOnly.includes(slide.id)) {
        return false;
      }
      return validSlideTypes.includes(slide.type);
    });

    slidesToReinsert.forEach(({ slide, beforeId }) => {
      const beforeIndex = updatedSlides.findIndex((s) => s.id === beforeId);
      const insertPosition =
        beforeIndex !== -1 ? beforeIndex : updatedSlides.length;
      updatedSlides.splice(insertPosition, 0, slide);
    });

    return [{ ...config, slides: updatedSlides }, removedSlides];
  }

  return [config, []];
};

function SliderOld() {
  const [config, setConfig] = useState<ConfigType | null>(null);
  const [filteredSlides, setFilteredSlides] = useState<SlideType[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [removedSlides, setRemovedSlides] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitModal, setSubmitModal] = useState(false);
  const configCopy = useRef<ConfigType | null>(null);
  const [modalShown, setModalShown] = useState<boolean>(false);
  const buttonClicked = useRef<boolean>(false);
  const [run, setRun] = useState(false);
  const [dataUpdated, setDataUpdated] = useState(false);
  const [consentCheckPoints, setConsentCheckPoints] = useState<any>({
    enpalMarketingConsent: false,
    thirdPartyMarketing: false,
    thirdPartyProfiling: false,
  });

  const { navigateForward, navigateBackward } = useBrowserHistory(
    "slideNavigation",
    true,
    () => setCurrentSlide((prev) => Math.max(0, prev - 1)),
    () =>
      hasHashParam() &&
      setCurrentSlide((prev) => Math.min(filteredSlides.length - 1, prev + 1))
  );

  function hasHashParam(): boolean {
    if (window.location.hash.length > 1) {
      if (payload.size > 0) return true;
      else return false;
    } else {
      if (payload.size > 0) return true;
    }
    return false;
  }

  const funnelStepRef = useRef(0);

  const sendAnalyticsData = (
    eventAction: string,
    eventLabel: string,
    eventValue?: string
  ) => {
    let value = eventValue;

    if (value === undefined) {
      if (eventAction === "Enpal Source Cookie") {
        value = "0";
        funnelStepRef.current = 0;
      } else if (eventAction === "slider-success") {
        value = "100";
      } else if (
        eventAction === "slider-failure" ||
        eventAction === "contact_form" ||
        eventAction.startsWith("PopOver") ||
        eventAction.startsWith("popover") ||
        eventAction === "OpenPopOverForm" ||
        eventAction === "marketing" ||
        eventAction === "thirdPartyMarketing" ||
        eventAction === "thirdPartyProfiling" ||
        eventAction === "contact_form_ThankYouPage"
      ) {
        value = undefined;
      } else {
        funnelStepRef.current += 1;
        value = String(funnelStepRef.current);
      }
    }

    sendGtagEvent("gaEvent", eventAction, eventLabel, value);
  };

  const setPayloadValue = (key: string, value: any) => {
    payload.set(key, value);
    const eventAction = filteredSlides[currentSlide]?.title || "unknown";
    if (
      filteredSlides[currentSlide]?.id !== "address_form" &&
      filteredSlides[currentSlide]?.id !== "nameFormSlide" &&
      filteredSlides[currentSlide]?.id !== "emailFormSlide" &&
      filteredSlides[currentSlide]?.id !== "phoneFormSlide" &&
      showModal === false
    )
      sendAnalyticsData(eventAction, value || "unknown");
  };

  function isAnswerSelected(slide: SlideType): boolean {
    if (slide?.apiFieldName && payload.has(slide.apiFieldName)) {
      const answer = payload.get(slide.apiFieldName);
      return answer !== undefined && answer !== null && answer !== "";
    }
    return false;
  }

  const filterSlides = (config: ConfigType | null) => {
    if (config && config.slides && currentSlide === 0) {
      const firstSlide = config.slides[0];

      let answer: any;
      if (firstSlide) answer = payload.get(firstSlide?.apiFieldName);

      if (firstSlide?.dependentSlides) {
        const filteredSlidesIds = firstSlide.dependentSlides
          .filter((depSlide: SlideType) => !depSlide.values.includes(answer))
          .map((item: SlideType) => item.id);

        const slidesToShow = config.slides.filter(
          (slide: SlideType) => !filteredSlidesIds.includes(slide.id)
        );

        setFilteredSlides(slidesToShow);
        configCopy.current = { ...config, slides: slidesToShow };
      } else {
        setFilteredSlides(filteredSlides);
        configCopy.current = config;
      }
    }
  };

  const hashEmail = async (email: any) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(email);

    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");

    return hashHex;
  };

  const filterSlider = (slides: any) => {
    if (!Array.isArray(slides)) {
      throw new Error("Invalid input: slides must be an array.");
    }
    const newRemovedSlides: any = [];
    const newFilteredArray = slides.filter((slide) => {
      if (
        slide.type === "SUCCESS_SLIDE" ||
        slide.type === "DISQUALIFYING_SLIDE"
      ) {
        newRemovedSlides.push(slide);
        return false;
      }
      return true;
    });

    setFilteredSlides(newFilteredArray);
    setRemovedSlides(newRemovedSlides);
  };

  useEffect(() => {
    if (run) filterSlider(filteredSlides);
  }, [run]);

  const processConfig = (initialConfig: ConfigType) => {
    const idsToRemoveAndAdd: { id: string; beforeId: string }[] = [];
    const idsToRemoveOnly = [""];
    setIsLoading(true);
    const [updatedConfig, removedSlides] = filterAndRemoveSlides(
      initialConfig,
      idsToRemoveAndAdd,
      idsToRemoveOnly
    );
    setConfig(updatedConfig);
    setRemovedSlides(removedSlides);
    filterSlides(updatedConfig);
    setFilteredSlides([...removedSlides, ...updatedConfig.slides]);
    setIsLoading(false);
    sendAnalyticsData("Enpal Source Cookie", utmParams.utm_source || "organic", "0");
  };

  useEffect(() => {
    if (config && !run) {
      processConfig(config);

      setRun(true);
    }
  }, [config]);

  const handleNavigation = (direction: any) => {
    if (currentSlide === 0) {
      filterSlides(config);
    }
    if (submitModal)
      if (direction === -2) {
        setCurrentSlide(0);
        setSubmitModal(!submitModal);
      }

    if (direction === 1) {
      navigateForward();
    } else if (currentSlide > 0) {
      navigateBackward();
    }
  };

  const submitData = () => {
    setIsLoading(true);
    const generatedId = new Types.ObjectId().toString();
    const timestamp = new Date().toISOString();
    const trackingData = {
      client_id: `${new Date().getTime()}-${Math.random()
        .toString(36)
        .substring(2)}`,
      conversion_url: window.location.href,
      entrance_url:
        sessionStorage.getItem("entrance_url") || window.location.href,
      url: window.location.href,
      useragent: navigator.userAgent,
      channel: utmParams.utm_source || "organic",
      campaign: utmParams.utm_campaign || "organic",
      ...utmParams,
    };

    // Extract agent codes from URL
    const urlParams = new URL(window.location.href);
    const agentCodePartner = urlParams.searchParams.get('agent_code_partner') || '';
    const agentCodeInstaller = urlParams.searchParams.get('agent_code_installer') || '';

    const fullPayload = {
      ...Object.fromEntries(payload),
      _id: generatedId,
      created_at: timestamp,
      updated_at: timestamp,
      form_submitted_at: timestamp,
      tracking_data: trackingData,
      country_code: config?.lang,
      uid: uniqueId,
      enpalMarketingConsent: consentCheckPoints.enpalMarketingConsent,
      thirdPartyMarketing: consentCheckPoints.thirdPartyMarketing,
      thirdPartyProfiling: consentCheckPoints.thirdPartyProfiling,
      agent_code_partner: agentCodePartner,
      agent_code_installer: agentCodeInstaller,
    } as { [key: string]: any };

    if (!fullPayload["contact[phone]"]) {
      setFilteredSlides((prevSlides: any) => {
        const updatedSlides = [...prevSlides, removedSlides[0]];
        const disqualifyingSlideIndex = updatedSlides.findIndex(
          (slide: any) => slide.type === "DISQUALIFYING_SLIDE"
        );
        if (disqualifyingSlideIndex !== -1) {
          setCurrentSlide(disqualifyingSlideIndex);
        }
        setIsLoading(false);
        return updatedSlides;
      });
      return;
    }

    fetch(
      config?.lang === "it"
        ? "https://enpal-salesos-prd-we-function.azurewebsites.net/api/send-data-ita?" // Italy API
        : "https://enpal-salesos-prd-we-function.azurewebsites.net/api/send-data-fra?", //France API
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        mode: "cors",
        body: JSON.stringify(fullPayload),
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to submit data");
        }
        console.log("Data submitted successfully");
        setFilteredSlides((prevSlides: any) => {
          const updatedSlides = [...prevSlides, removedSlides[1]];
          const successSlideIndex = updatedSlides.findIndex(
            (slide: any) => slide.type === "SUCCESS_SLIDE"
          );
          if (successSlideIndex !== -1) {
            setCurrentSlide(successSlideIndex);
          }
          setIsLoading(false);
          return updatedSlides;
        });
        sendAnalyticsData("slider-success", "form_submit_button", "100");
        setTimeout(async () => {
          const data =
            fullPayload["contact[email]"] !== undefined
              ? {
                  sha256_email_address: await hashEmail(
                    fullPayload["contact[email]"]
                  ),
                }
              : fullPayload["contact[phone]"] !== undefined
              ? {
                  sha256_phone_number: await hashEmail(
                    fullPayload["contact[phone]"]
                  ),
                }
              : null;
          if (data) {
            sendLeadEvent("enpal-lead-created", data);
          }
        }, 100);
      })
      .catch((error) => {
        console.error("Error submitting data:", error);

        setFilteredSlides((prevSlides: any) => {
          const updatedSlides = [...prevSlides, removedSlides[0]];
          const disqualifyingSlideIndex = updatedSlides.findIndex(
            (slide: any) => slide.type === "DISQUALIFYING_SLIDE"
          );
          sendAnalyticsData("slider-failure", "form_submit_button");
          if (disqualifyingSlideIndex !== -1) {
            setCurrentSlide(disqualifyingSlideIndex);
          }
          setIsLoading(false);
          return updatedSlides;
        });
      });
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(
        "https://enpal-salesos-prd-we-function.azurewebsites.net/api/update-data-ita",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid: uniqueId,
            enpalMarketingConsent: consentCheckPoints?.enpalMarketingConsent,
            thirdPartyMarketing: consentCheckPoints?.thirdPartyMarketing,
            thirdPartyProfiling: consentCheckPoints?.thirdPartyProfiling,
          }),
        }
      );

      const result = await response.json();
      if (response.ok) setDataUpdated(true);
      console.log(result.message || "Data updated successfully!");
    } catch (error) {
      console.log("Error updating data", error);
    }
  };

  useEffect(() => {
    if (currentSlide === 0) {
      const removeLastHistoryEntry = () => {
        navigateBackward();
      };

      removeLastHistoryEntry();
    }
  }, [currentSlide, navigateBackward]);

  const closeModal = () => {
    setShowModal(false);
  };

  const getSlideByType = (slideData: any) => {
    switch (slideData.type) {
      case "DISQUALIFYING_SLIDE":
        return (
          <DisqualifyingSlide
            submitModal={submitModal}
            data={slideData}
            onNavigation={handleNavigation}
          />
        );
      case "SINGLE_ANSWER_SLIDE":
      case "MULTI_ANSWER_SLIDE":
        return (
          <AnswerSlide
            showModal={showModal}
            data={slideData}
            onNavigation={handleNavigation}
            setPayloadValue={setPayloadValue}
            setShowModal={setShowModal}
            config={config}
            setModalShown={setModalShown}
            modalShown={modalShown}
            sendAnalyticsData={sendAnalyticsData}
            buttonClicked={buttonClicked}
          />
        );
      case "ZIP_CODE_SLIDE":
        return (
          <ZipCodeSlide
            data={slideData}
            onNavigation={handleNavigation}
            setPayloadValue={setPayloadValue}
          />
        );
      case "INTERIM_ANIMATION_SLIDE":
        return (
          <InterimAnimationSlide
            data={slideData}
            inView={filteredSlides?.indexOf(slideData) === currentSlide}
            onNavigation={handleNavigation}
          />
        );
      case "FORM_SLIDE":
        return (
          <FormSlide
            data={slideData}
            onNavigation={handleNavigation}
            setPayloadValue={setPayloadValue}
          />
        );
      case "ELECTRICITY_USE_SLIDE":
        return (
          <ElectricityUseSlide
            data={slideData}
            onNavigation={handleNavigation}
          />
        );
      case "FREE_TEXT_SLIDE":
        return (
          <FreeTextSlide data={slideData} onNavigation={handleNavigation} />
        );
      case "SUCCESS_SLIDE":
        return (
          <SuccessSlide
            data={slideData}
            onNavigation={handleNavigation}
            setConsentCheckPoints={setConsentCheckPoints}
            handleUpdate={handleUpdate}
            dataUpdated={dataUpdated}
            sendAnalyticsData={sendAnalyticsData}
            sendGtagEvent={sendGtagEvent}
          />
        );
      case "SINGLE_ACTION_SLIDE":
        return (
          <SingleActionSlide data={slideData} onNavigation={handleNavigation} />
        );
      case "IMAGE_UPLOAD_SLIDE":
        return (
          <ImageUploadSlide data={slideData} onNavigation={handleNavigation} />
        );
      case "ADDRESS_FORM_SLIDE":
        return (
          <AddressFormSlide
            data={slideData}
            onNavigation={handleNavigation}
            setPayloadValue={setPayloadValue}
            sendAnalyticsData={sendAnalyticsData}
          />
        );
      case "SEPARATED_FORM_SLIDE":
        return (
          <SeparateFormSlide
            data={slideData}
            onNavigation={handleNavigation}
            submitData={submitData}
            setPayloadValue={setPayloadValue}
            sendAnalyticsData={sendAnalyticsData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.App}>
      <GTMComponent
        onConfigLoad={(config: any) => {
          setConfig(config);
          configCopy.current = { ...config };
          setIsLoading(false);
        }}
      />
      {(filteredSlides[currentSlide]?.type === "SINGLE_ANSWER_SLIDE" ||
        filteredSlides[currentSlide]?.type === "MULTI_ANSWER_SLIDE") &&
        config?.modalForm &&
        showModal && (
          <PopoverForm
            showModal={showModal}
            setSubmitModal={setSubmitModal}
            setShowModal={setShowModal}
            setPayloadValue={setPayloadValue}
            onSubmit={submitData}
            options={config?.slides[1].answers}
            data={config?.modalForm}
            closeModal={closeModal}
            sendAnalyticsData={sendAnalyticsData}
            setConsentCheckPoints={setConsentCheckPoints}
          />
        )}

      <div className={styles.container}>
        {isLoading ? (
          <div className={styles.loading}></div>
        ) : (
          <div className={styles.slideContainer}>
            {filteredSlides.map((slide: SlideType, index: number) => (
              <div
                key={slide.id}
                className={
                  currentSlide === index
                    ? styles.visible
                    : styles.hideAfterDelay
                }
                style={{
                  transform: `translateX(${-currentSlide * 100}%)`,
                }}>
                {getSlideByType(slide)}
                {(slide.type === "SINGLE_ANSWER_SLIDE" ||
                  slide.type === "MULTI_ANSWER_SLIDE") && (
                  <div className={styles.navigateButton}>
                    <SlideNavigation
                      onNavigation={handleNavigation}
                      backText={
                        filteredSlides[currentSlide]?.id ===
                        filteredSlides[0]?.id
                          ? ""
                          : config?.defaultNavigation?.backButtonLabel
                      }
                      nextText={config?.defaultNavigation?.nextButtonLabel}
                      selected={
                        isAnswerSelected(filteredSlides[currentSlide])
                          ? [1]
                          : []
                      }
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SliderOld;

